import axios, {
  AxiosInstance,
  AxiosRequestConfig,
  AxiosResponse,
  InternalAxiosRequestConfig,
} from 'axios'
import type { ApiResponse, RequestError, RequestErrorDetail } from '@/types/api'
import { syncServerTimeFromDateHeader } from '@/utils/serverTime'
import { getMessage } from '@/utils/antdStatic'

/** 扩展 axios config，支持跳过全局错误 toast */
declare module 'axios' {
  interface AxiosRequestConfig {
    skipErrorToast?: boolean
  }
}

/**
 * 会话过期处理是否正在进行中。
 * 页面首屏常并发多个请求，登录态过期时会一起返回 401，
 * 用此标志确保「清会话 + 过期提示」只执行一次，避免重复弹窗与重复跳转。
 */
let sessionExpiryHandled = false

/**
 * 处理登录态过期（401）：清空本地会话并提示用户重新登录。
 * 清空 authStore 后，订阅 isAuthenticated 的 RouteGuard 会自动
 * `Navigate('/login?redirect=<当前路径>', replace)`，因此这里无需手动跳转，
 * 走 SPA 跳转而非整页刷新。toast 用固定 key 去重，标志短延时后复位以支持「重新登录后再次过期」。
 */
async function handleSessionExpired(expiredMessage: string): Promise<void> {
  if (sessionExpiryHandled) return
  sessionExpiryHandled = true
  const { default: useAuthStore } = await import('@/store/authStore')
  useAuthStore.getState().clearSession()
  getMessage().error({ content: expiredMessage, key: 'session-expired' })
  setTimeout(() => {
    sessionExpiryHandled = false
  }, 3000)
}

/** 当前是否停留在登录页 —— 登录页内的 401（二步校验等）不触发会话过期跳转，避免重定向循环。 */
function isOnLoginPage(): boolean {
  return window.location.pathname.startsWith('/login')
}

function makeRequestError(
  code: number,
  type: RequestError['type'],
  msg: string = '',
  raw?: unknown,
  details?: RequestErrorDetail[]
): RequestError {
  return { code, type, message: msg, raw, details }
}

/** 从响应体中解析 detail 数组（兼容顶层 detail 与 error.detail）。 */
function extractErrorDetails(data: any): RequestErrorDetail[] {
  const raw = Array.isArray(data?.detail)
    ? data.detail
    : Array.isArray(data?.error?.detail)
      ? data.error.detail
      : []
  return raw
    .map((d: any) => ({
      field: typeof d?.field === 'string' && d.field ? d.field : undefined,
      message: typeof d?.message === 'string' ? d.message : '',
    }))
    .filter((d: RequestErrorDetail) => d.message)
}

/**
 * 从接口响应体中解析标准化错误信息。
 * message 取接口顶层信息，优先 data.message > error.message，
 * 仅当两者皆缺时才回退到 detail[0]，避免顶层 message 被 detail 覆盖。
 * details 单独结构化保留，供表单等场景逐条展示。
 */
function extractError(
  data: any,
  fallback = ''
): { message: string; details: RequestErrorDetail[] } {
  const details = extractErrorDetails(data)
  let message: string
  if (typeof data?.message === 'string' && data.message) {
    message = data.message
  } else if (typeof data?.error?.message === 'string' && data.error.message) {
    message = data.error.message
  } else if (details.length > 0) {
    message = details[0].message
  } else {
    message = fallback
  }
  return { message, details }
}

export function setupInterceptors(instance: AxiosInstance): void {
  instance.interceptors.request.use(
    async (config: InternalAxiosRequestConfig) => {
      config.headers = config.headers ?? {}
      const { default: useAuthStore } = await import('@/store/authStore')
      const token = useAuthStore.getState().token
      if (token) {
        config.headers['Authorization'] = `Bearer ${token}`
      }
      // 按用户所选语言注入 Accept-Language，后端据此返回本地化内容
      const { default: useI18nStore } = await import('@/store/i18nStore')
      const locale = useI18nStore.getState().locale
      if (locale) {
        config.headers['Accept-Language'] = locale
      }
      return config
    },
    (error: unknown) => Promise.reject(error)
  )

  instance.interceptors.response.use(
    (response: AxiosResponse<ApiResponse<unknown>>) => {
      // 用响应头 Date 校准服务器时间，供期权倒计时等场景使用
      syncServerTimeFromDateHeader(response.headers?.['date'] as string | undefined)
      // 204 No Content（如 DELETE 成功）：无响应体可解包，直接放行
      if (response.status === 204) {
        return undefined as unknown as AxiosResponse
      }
      const apiResponse = response.data as any
      // 兼容两种响应格式：
      // 新格式: { error, data }
      // 真实接口格式: { success, message, data, request_id }
      if ('error' in apiResponse && apiResponse.error) {
        const { message: errMsg, details } = extractError(apiResponse, 'Request failed')
        getMessage().error(errMsg)
        return Promise.reject(
          makeRequestError(apiResponse.error.code, 'http', errMsg, response, details)
        )
      }
      if ('success' in apiResponse && !apiResponse.success) {
        const { message: errMsg, details } = extractError(apiResponse, 'Request failed')
        getMessage().error(errMsg)
        return Promise.reject(makeRequestError(0, 'http', errMsg, response, details))
      }
      return apiResponse.data as unknown as AxiosResponse
    },
    async (error: unknown) => {
      const { default: i18n } = await import('@/locales')
      const t = i18n.t.bind(i18n)

      if (axios.isCancel(error)) {
        return Promise.reject(makeRequestError(0, 'cancel', 'Request cancelled', error))
      }

      if (axios.isAxiosError(error)) {
        if (error.code === 'ECONNABORTED') {
          getMessage().error(t('common.error.timeout'))
          return Promise.reject(makeRequestError(0, 'timeout', 'Request timeout', error))
        }

        if (!error.response) {
          getMessage().error(t('common.error.network'))
          return Promise.reject(makeRequestError(0, 'network', 'Network error', error))
        }

        const status = error.response.status
        const responseData = error.response.data as any
        const skipToast = (error.config as AxiosRequestConfig)?.skipErrorToast === true

        // 顶层 message 优先，detail 结构化保留供表单逐条展示
        const { message: serverMessage, details } = extractError(responseData)

        if (status === 401) {
          if (isOnLoginPage()) {
            // 登录页内的 401（凭证/二步校验失败等）：仅按需提示，由登录页自行处理，
            // 不清会话、不跳转，避免在登录页上重定向循环。
            if (!skipToast) getMessage().error(serverMessage || t('request.error.unauthenticated'))
          } else {
            // 登录态中途过期：清空本地会话并提示重新登录，RouteGuard 据此带 redirect 跳登录页。
            await handleSessionExpired(t('request.error.sessionExpired'))
          }
          return Promise.reject(
            makeRequestError(
              401,
              'http',
              serverMessage || t('request.error.unauthenticated'),
              error,
              details
            )
          )
        }

        if (status === 403) {
          if (!skipToast) getMessage().error(t('request.error.unauthorized'))
          return Promise.reject(
            makeRequestError(403, 'http', serverMessage || 'Forbidden', error, details)
          )
        }

        if (status >= 500 && status <= 599) {
          if (!skipToast) getMessage().error(t('common.error.server'))
          return Promise.reject(
            makeRequestError(
              status,
              'http',
              serverMessage || `Server error ${status}`,
              error,
              details
            )
          )
        }

        const displayMessage = serverMessage || `Request failed with status ${status}`
        if (!skipToast) getMessage().error(displayMessage)
        return Promise.reject(makeRequestError(status, 'http', displayMessage, error, details))
      }

      getMessage().error(t('common.error.unknown'))
      return Promise.reject(makeRequestError(0, 'network', 'Unknown error', error))
    }
  )
}
